/**
 * What every card stands on. The concept video lays each card out on a fixed
 * 1080 by 1920 stage. Phones are taller than that now (about 9:19.5, and a
 * browser's bars change it as they come and go), so here the stage keeps its
 * 1080 width but grows taller to fill the screen, from 1920 (9:16) to 2400
 * (9:20). A card puts its pieces in zones: the top stays put, the bottom
 * follows the bottom edge, the middle stays centred, so a taller screen
 * spreads a card out instead of leaving empty bands. `Scene` paints a card's
 * drifting blobs, and the rest are the small pieces most cards share.
 */

import {
  createContext,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from "react";

import { prefersReducedMotion } from "../motion";
import { PERSON_COLOURS, wr } from "../theme";

export const W = 1080;
/** The shortest stage, 9:16: every card is designed at this height. */
export const H = 1920;
/** The tallest, 9:20. Taller screens get thin bands above and below. */
export const H_MAX = 2400;

const StageHeight = createContext(H);
/** How tall the stage is on this screen, in stage pixels (H to H_MAX). */
export const useStageHeight = () => useContext(StageHeight);
/** How much taller than the designed 1920 the stage is on this screen. */
export const useStageExtra = () => useContext(StageHeight) - H;

/** The stage's size and place for a box this big: fill the width, grow to fill the height. */
export function fitStage(width: number, height: number) {
  let scale = width / W;
  let tall = height / scale;
  if (tall < H) {
    // Wider than 9:16 (a landscape phone, a laptop window): fit the height, centred.
    scale = height / H;
    tall = H;
  } else if (tall > H_MAX) {
    tall = H_MAX;
  }
  return { scale, height: tall, x: (width - W * scale) / 2, y: (height - tall * scale) / 2 };
}

/** Scales the stage into its parent and makes it as tall as the parent's shape allows. */
export function Stage({ children }: { children: ReactNode }) {
  const box = useRef<HTMLDivElement>(null);
  // Nothing goes on the stage until it knows its height, so a card's timeline is
  // built for the screen it's on. (It's measured before the first paint, so no flash.)
  const [fit, setFit] = useState<ReturnType<typeof fitStage> | null>(null);

  useLayoutEffect(() => {
    const el = box.current;
    if (!el) return;
    const measure = () => {
      const { width, height } = el.getBoundingClientRect();
      // No size to measure (a test, or a hidden tab): stand at the designed height.
      if (!width || !height) {
        setFit((was) => was ?? { scale: 1, height: H, x: 0, y: 0 });
        return;
      }
      setFit(fitStage(width, height));
    };
    measure();
    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", measure);
      return () => window.removeEventListener("resize", measure);
    }
    const watch = new ResizeObserver(measure);
    watch.observe(el);
    return () => watch.disconnect();
  }, []);

  return (
    <div ref={box} className="wr-stage-box">
      {fit ? (
        <div
          className="wr-stage"
          style={{ height: fit.height, transform: `translate(${fit.x}px, ${fit.y}px) scale(${fit.scale})` }}
        >
          <StageHeight.Provider value={fit.height}>{children}</StageHeight.Provider>
        </div>
      ) : null}
    </div>
  );
}

/**
 * Part of a card that keeps its place relative to one edge as the stage grows.
 * Everything inside is laid out in the designed 1920-tall stage, as before; on
 * a taller screen "top" stays put, "bottom" moves down with the bottom edge,
 * "middle" stays centred, and a number from 0 to 1 sits that far between.
 */
export function Zone({ at, children }: { at: "top" | "middle" | "bottom" | number; children: ReactNode }) {
  const extra = useStageExtra();
  const share = at === "top" ? 0 : at === "middle" ? 0.5 : at === "bottom" ? 1 : at;
  const shift = Math.round(extra * share);
  return (
    <div className="wr-zone" style={shift ? { transform: `translateY(${shift}px)` } : undefined}>
      {children}
    </div>
  );
}

// ---------- blobs ----------

export type BlobSpec = {
  /** A colour name from `wrapped.css`, e.g. "yellow". */
  colour: string;
  x: number;
  y: number;
  r: number;
  seed: number;
  /** How much the outline wobbles; the video uses 0.14 to 0.24. */
  wob?: number;
  opacity?: number;
  /** Grow out of this point as the card opens (the intro's burst). */
  from?: { x: number; y: number; delay: number };
};

/** A smooth closed blob: a wobbling circle through twelve points, joined with Catmull-Rom curves. */
export function blobPath(cx: number, cy: number, r: number, t: number, seed: number, wob: number): string {
  const n = 12;
  const pts: [number, number][] = [];
  for (let i = 0; i < n; i++) {
    const a = (i / n) * Math.PI * 2;
    const k =
      1 +
      wob *
        (0.55 * Math.sin(2 * a + t * 0.8 + seed) +
          0.3 * Math.sin(3 * a - t * 1.1 + seed * 1.7) +
          0.15 * Math.sin(5 * a + t * 1.6 + seed * 2.3));
    pts.push([cx + Math.cos(a) * r * k, cy + Math.sin(a) * r * k]);
  }
  const f = (v: number) => v.toFixed(1);
  let d = `M${f(pts[0][0])} ${f(pts[0][1])}`;
  for (let i = 0; i < n; i++) {
    const p0 = pts[(i - 1 + n) % n], p1 = pts[i], p2 = pts[(i + 1) % n], p3 = pts[(i + 2) % n];
    d += `C${f(p1[0] + (p2[0] - p0[0]) / 6)} ${f(p1[1] + (p2[1] - p0[1]) / 6)} ${f(p2[0] - (p3[0] - p1[0]) / 6)} ${f(p2[1] - (p3[1] - p1[1]) / 6)} ${f(p2[0])} ${f(p2[1])}`;
  }
  return `${d}Z`;
}

const elasticOut = (t: number) => (t <= 0 ? 0 : t >= 1 ? 1 : 2 ** (-10 * t) * Math.sin(((t * 10 - 0.75) * 2 * Math.PI) / 3.6) + 1);
const power3Out = (t: number) => 1 - (1 - Math.min(Math.max(t, 0), 1)) ** 3;

function blobAt(b: BlobSpec, t: number): string {
  let { x, y, r } = b;
  if (b.from) {
    const local = t - b.from.delay;
    const f = power3Out(local / 1.3);
    x = b.from.x + (x - b.from.x) * f;
    y = b.from.y + (y - b.from.y) * f;
    r = Math.max(0.5, r * elasticOut(local / 1.6));
  }
  const dx = 40 * Math.sin(t * 0.35 + b.seed);
  const dy = 40 * Math.cos(t * 0.28 + b.seed * 1.3);
  return blobPath(x + dx, y + dy, r, t, b.seed, b.wob ?? 0.14);
}

/** The card's blobs, drifting and wobbling for as long as the card is up. */
function Blobs({ blobs: designed }: { blobs: BlobSpec[] }) {
  // A blob in the lower half belongs to the bottom edge: on a taller stage it moves down with it.
  const extra = useStageExtra();
  const blobs = useMemo(
    () => (extra ? designed.map((b) => (b.y > H / 2 ? { ...b, y: b.y + extra } : b)) : designed),
    [designed, extra],
  );
  const paths = useRef<(SVGPathElement | null)[]>([]);
  const paused = useStoryPaused();
  const clock = useRef(1);

  useEffect(() => {
    if (prefersReducedMotion() || paused) return;
    let frame = 0;
    let last = performance.now();
    const draw = (now: number) => {
      clock.current += Math.min(now - last, 100) / 1000;
      last = now;
      blobs.forEach((b, i) => paths.current[i]?.setAttribute("d", blobAt(b, clock.current)));
      frame = requestAnimationFrame(draw);
    };
    frame = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(frame);
  }, [blobs, paused]);

  return (
    <svg className="blobs" viewBox={`0 0 ${W} ${H + extra}`} width={W} height={H + extra} aria-hidden>
      {blobs.map((b, i) => (
        <path
          key={i}
          ref={(el) => {
            paths.current[i] = el;
          }}
          // The still frame: grown blobs at rest, which is also what reduced motion shows.
          d={blobAt({ ...b, from: undefined }, 1)}
          fill={wr(b.colour)}
          opacity={b.opacity ?? 1}
        />
      ))}
    </svg>
  );
}

/** One card: its blobs at the back, then everything it lays out in stage pixels. */
export function Scene({ blobs = [], children, style }: { blobs?: BlobSpec[]; children?: ReactNode; style?: CSSProperties }) {
  return (
    <div className="scene" style={style}>
      <Blobs blobs={blobs} />
      <div className="layer">{children}</div>
    </div>
  );
}

// ---------- the player's state, for cards that animate ----------

const Paused = createContext(false);
export const PausedProvider = Paused.Provider;
/** True while the story is paused, so a card's animation can wait with it. */
export const useStoryPaused = () => useContext(Paused);

// ---------- people ----------

const Roster = createContext<string[]>([]);
const Pictures = createContext<Record<string, string>>({});

/**
 * Everyone in the recap, in the order their colours are handed out, and the
 * address of each one's profile picture where the server has it.
 */
export function RosterProvider({
  names,
  avatars = {},
  children,
}: {
  names: string[];
  avatars?: Record<string, string>;
  children: ReactNode;
}) {
  return (
    <Roster.Provider value={names}>
      <Pictures.Provider value={avatars}>{children}</Pictures.Provider>
    </Roster.Provider>
  );
}

/** A person's profile picture on this server, or null to draw their letter instead. */
export function usePictureOf(): (name: string) => string | null {
  const pictures = useContext(Pictures);
  return (name: string) => pictures[name] ?? null;
}

const hash = (s: string) => [...s].reduce((h, c) => (h * 31 + c.charCodeAt(0)) >>> 0, 7);

const CardColour = createContext<string | null>(null);
/** The colour of the card being drawn, so nobody's avatar vanishes into it. */
export const CardColourProvider = CardColour.Provider;

/**
 * A person's colour: their place in the roster, or a steady pick for anyone
 * outside it. On a card of that same colour they borrow one nobody else has.
 */
export function useColourOf(): (name: string) => string {
  const roster = useContext(Roster);
  const card = useContext(CardColour);
  return (name: string) => {
    const at = roster.indexOf(name);
    const pick = (at >= 0 ? at : hash(name)) % PERSON_COLOURS.length;
    const colour = PERSON_COLOURS[pick];
    if (colour !== card) return colour;
    const spare = PERSON_COLOURS.find((c, i) => i >= roster.length && c !== card);
    return spare ?? PERSON_COLOURS[(pick + 1) % PERSON_COLOURS.length];
  };
}

/** A round avatar: the person's colour and first letter (the recap has no pictures). */
export function Avatar({
  name,
  size,
  inline = false,
  className = "",
  style,
}: {
  name: string;
  size?: number;
  inline?: boolean;
  className?: string;
  style?: CSSProperties;
}) {
  const colourOf = useColourOf();
  const picture = usePictureOf()(name);
  // A picture that won't load (gone from the server, say) falls back to the letter.
  const [broken, setBroken] = useState<string | null>(null);
  const sized = size ? { width: size, height: size, fontSize: Math.round(size * 0.55) } : {};
  return (
    <div
      aria-hidden
      className={`av${inline ? " inl" : ""} ${className}`}
      style={{ background: colourOf(name), ...sized, ...style }}
    >
      {picture && broken !== picture ? (
        <img
          src={picture}
          alt=""
          draggable={false}
          onError={() => setBroken(picture)}
          style={{ width: "100%", height: "100%", borderRadius: "50%", objectFit: "cover" }}
        />
      ) : (
        ([...name][0]?.toUpperCase() ?? "?")
      )}
    </div>
  );
}

// ---------- text ----------

/** Text split into letters (class `ch`) grouped by word, for letter-by-letter entrances. */
export function Letters({ text, colours }: { text: string; colours?: string[] }) {
  let at = 0;
  const words = text.split(" ");
  return (
    <>
      {words.map((word, w) => (
        <span key={w}>
          <span className="wd">
            {[...word].map((c, i) => {
              const colour = colours?.[at++];
              return (
                <span key={i} className="ch" style={colour ? { color: colour } : undefined}>
                  {c}
                </span>
              );
            })}
          </span>
          {w < words.length - 1 ? " " : null}
        </span>
      ))}
    </>
  );
}

/** Text split into words (class `wd`), for word-by-word entrances. */
export function Words({ text }: { text: string }) {
  const words = text.split(" ");
  return (
    <>
      {words.map((word, i) => (
        <span key={i}>
          <span className="wd">{word}</span>
          {i < words.length - 1 ? " " : null}
        </span>
      ))}
    </>
  );
}

/**
 * One line of big text that shrinks until it fits its box: real names and
 * award titles run longer than the video's. Give it a width (or left and
 * right); `size` is the largest font size, `min` the smallest it goes to.
 */
export function Fit({
  size,
  min = Math.round(size * 0.4),
  className = "",
  style,
  children,
}: {
  size: number;
  min?: number;
  className?: string;
  style?: CSSProperties;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [font, setFont] = useState(size);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    let next = size;
    el.style.fontSize = `${next}px`;
    // jsdom has no layout: widths read 0 there, so the size stays as given.
    while (next > min && el.scrollWidth > el.clientWidth + 1) {
      next = Math.max(min, Math.floor(next * 0.94));
      el.style.fontSize = `${next}px`;
    }
    setFont(next);
  }, [size, min, children]);

  return (
    <div ref={ref} className={className} style={{ whiteSpace: "nowrap", ...style, fontSize: font }}>
      {children}
    </div>
  );
}
